'use client';

import { useCallback, useMemo, useRef, useState } from 'react';

import {
  CitedAnswer,
  type CitedSource,
} from '@/components/ui/cited-answer-clean';
import { getCitationExcerpt } from '@/services/connie';
import type { LibraryReference } from '@/types/chat';

import { buildCitedAnswer, type CitedAnswerModel } from '../utils/citedAnswer';

type Excerpt = { quote: string; highlight: string[] };

const LOADING_QUOTE = 'Reading the page…';
const UNAVAILABLE_QUOTE =
  'The passage is not available here. Open the document to read this page.';

type Props = {
  content: string;
  references: readonly LibraryReference[];
  /** The session and turn the excerpt endpoint needs. Without them, no preview. */
  sessionId?: string | null;
  turnIndex?: number;
  model?: CitedAnswerModel;
};

/**
 * Connie's reply as affectUI Cited Answer: markers at the end of each cited
 * sentence, one chip per cited page, and a preview that fetches the passage behind
 * the citation on demand through Xano and FSI (VH3_CONNECT #549). The passage is
 * kept in component state only.
 */
export function CitedAnswerMessage({
  content,
  references,
  sessionId,
  turnIndex,
  model: given,
}: Props) {
  const model = useMemo(
    () => given ?? buildCitedAnswer(content, references),
    [given, content, references]
  );
  const [excerpts, setExcerpts] = useState<Record<string, Excerpt | null>>({});
  const inFlight = useRef(new Set<string>());
  const canFetch = Boolean(sessionId) && typeof turnIndex === 'number';

  const handlePreviewChange = useCallback(
    (id: string | null) => {
      if (!id || !canFetch || !sessionId || typeof turnIndex !== 'number') {
        return;
      }
      if (id in excerpts || inFlight.current.has(id)) return;
      const page = model.pages[id];
      if (!page) return;
      inFlight.current.add(id);
      getCitationExcerpt({
        sessionId,
        turnIndex,
        documentId: page.documentId,
        page: page.page,
      })
        .then(result => {
          setExcerpts(previous => ({
            ...previous,
            [id]: result?.excerpt
              ? { quote: result.excerpt, highlight: result.highlight ?? [] }
              : null,
          }));
        })
        .catch(() => {
          setExcerpts(previous => ({ ...previous, [id]: null }));
        })
        .finally(() => {
          inFlight.current.delete(id);
        });
    },
    [canFetch, excerpts, model.pages, sessionId, turnIndex]
  );

  // The card reads the host from an absolute URL; the Library href is relative.
  const origin =
    typeof window === 'undefined' ? undefined : window.location.origin;
  const sources = useMemo<CitedSource[]>(
    () =>
      model.sources.map(relative => {
        const source =
          origin && relative.url
            ? { ...relative, url: new URL(relative.url, origin).toString() }
            : relative;
        const excerpt = excerpts[source.id];
        if (excerpt) {
          return {
            ...source,
            quote: excerpt.quote,
            highlight: excerpt.highlight.length ? excerpt.highlight : undefined,
          };
        }
        if (excerpt === null || !canFetch) {
          return { ...source, quote: UNAVAILABLE_QUOTE, highlight: [] };
        }
        return { ...source, quote: LOADING_QUOTE, highlight: [] };
      }),
    [model.sources, excerpts, canFetch, origin]
  );

  const openSource = useCallback((source: CitedSource) => {
    if (source.url) window.open(source.url, '_blank', 'noopener,noreferrer');
  }, []);

  return (
    // The card sizes itself as a CSS container (container-type: inline-size), so it
    // has no intrinsic width: the wrapper and the message column give it one.
    <div
      data-theme='dark'
      data-cy='connie-cited-answer'
      className='connie-cited-answer w-full min-w-0'
    >
      <CitedAnswer
        segments={model.segments}
        sources={sources}
        pagesRead={model.pagesRead || undefined}
        onOpenSource={openSource}
        onPreviewChange={handlePreviewChange}
      />
    </div>
  );
}
